/**
 * Shared template renderer for admin-editable email templates.
 * Mirrors the logic used by the send-email edge function.
 */

export interface EmailPlaceholder {
  token: string;
  label: string;
  description: string;
  example: string;
}

export const EMAIL_TEMPLATE_PLACEHOLDERS: EmailPlaceholder[] = [
  { token: "order_id", label: "Order ID", description: "External order ID, or unique ID fallback", example: "AURORA-1234" },
  { token: "unique_id", label: "Unique ID", description: "Aurora-assigned order ID", example: "AURORA-1234" },
  { token: "status", label: "Status", description: "Human label of the new order status", example: "In Transit" },
  { token: "origin", label: "Origin", description: "Ship-from city, state", example: "Los Angeles, CA" },
  { token: "destination", label: "Destination", description: "Ship-to city, state", example: "Phoenix, AZ" },
  { token: "departed_at", label: "Departed At", description: "Actual departure time (LA tz)", example: "Mon, Jun 2, 9:14 AM PDT" },
  { token: "arrived_at", label: "Arrived At", description: "Actual arrival time (LA tz)", example: "Mon, Jun 2, 4:02 PM PDT" },
  { token: "scheduled_delivery", label: "Scheduled Delivery", description: "Requested delivery time end (LA tz)", example: "Mon, Jun 2, 5:00 PM PDT" },
  { token: "cancellation_reason", label: "Cancellation Reason", description: "Reason an order was canceled", example: "Customer request" },
  { token: "customer_name", label: "Customer Name", description: "Billing customer organization name", example: "Acme Logistics" },
  { token: "customer_short_code", label: "Customer Short Code", description: "Billing customer short code", example: "ACME" },
  { token: "trailer_name", label: "Trailer", description: "Trailer ID assigned to the order", example: "T-4821" },
];

const TOKEN_RE = /\{([a-zA-Z0-9_]+)\}/g;

export interface RenderResult {
  output: string;
  unknownTokens: string[];
}

export function renderTemplate(
  template: string,
  vars: Record<string, string | null | undefined>,
): RenderResult {
  const unknown = new Set<string>();
  const output = template.replace(TOKEN_RE, (match, key: string) => {
    if (Object.prototype.hasOwnProperty.call(vars, key)) {
      const v = vars[key];
      return v == null || v === "" ? "—" : String(v);
    }
    unknown.add(key);
    return match;
  });
  return { output, unknownTokens: Array.from(unknown) };
}

export const EMAIL_CATEGORIES = [
  { value: "order_in_transit", label: "Order In Transit", defaultSubject: "Order {order_id} is in transit", defaultBody: "Hi {customer_short_code} team,\n\nOrder {order_id} ({origin} → {destination}) has departed and is now in transit.\nDeparted: {departed_at}\nScheduled delivery: {scheduled_delivery}\n\n— Aurora" },
  { value: "order_delivered", label: "Order Delivered", defaultSubject: "Order {order_id} has been delivered", defaultBody: "Hi {customer_short_code} team,\n\nOrder {order_id} ({origin} → {destination}) was delivered {arrived_at}.\n\n— Aurora" },
  { value: "order_canceled", label: "Order Canceled", defaultSubject: "Order {order_id} has been canceled", defaultBody: "Hi {customer_short_code} team,\n\nOrder {order_id} ({origin} → {destination}) has been canceled.\nReason: {cancellation_reason}\n\n— Aurora" },
] as const;

export type EmailCategory = (typeof EMAIL_CATEGORIES)[number]["value"];

export const EXAMPLE_VARS: Record<string, string> = Object.fromEntries(
  EMAIL_TEMPLATE_PLACEHOLDERS.map((p) => [p.token, p.example]),
);